<template>
  <div :class="{ 'ntf-row--unread': !notification.readAt, 'ntf-row--link': canOpen, 'ntf-row--opening': opening }"
       :role="canOpen ? 'link' : undefined" :tabindex="canOpen ? 0 : undefined" class="ntf-row" @click="open"
       @keydown.enter.self="open">
    <span :class="`ntf-row__icon--${view.tone}`" class="ntf-row__icon">
      <v-icon small>{{ view.icon }}</v-icon>
    </span>
    <div class="ntf-row__body">
      <div class="ntf-row__title">
        {{ view.title }}
        <span v-if="count > 1" class="ntf-row__count" :title="`${count}× gemeldet`">{{ count }}×</span>
      </div>
      <div v-if="view.text" class="ntf-row__text">{{ view.text }}</div>
      <div v-for="(line, index) in view.lines" :key="index" class="ntf-row__line">{{ line }}</div>
      <div v-if="view.hint" :class="`ntf-row__hint--${view.hint.tone}`" class="ntf-row__hint">{{ view.hint.text }}</div>
      <div class="ntf-row__time">
        <v-icon v-if="notification.dismissedAt" x-small>mdi-delete-outline</v-icon>
        {{ time }}
      </div>
      <div v-if="toDoActions" class="ntf-row__todo" @click.stop @keydown.stop>
        <span v-if="toDoDone" class="ntf-chip" title="Die Aufgabe wurde bereits erledigt"><v-icon x-small>mdi-check-all</v-icon> erledigt</span>
        <button v-if="toDoAccess.complete && !toDoDone" :disabled="busy" class="ntf-chip ntf-chip--go" type="button" @click="$emit('complete', notification)">
          <v-icon x-small>mdi-check</v-icon>
          Erledigen
        </button>
        <v-menu v-if="toDoAccess.snooze && !toDoDone" left offset-y>
          <template v-slot:activator="{ on, attrs }">
            <button :disabled="busy" class="ntf-chip" type="button" v-bind="attrs" v-on="on">Neues Datum</button>
          </template>
          <v-list class="soft-menu py-1" dense>
            <v-list-item v-for="option in toDoSnoozeOptions" :key="option.value" @click="$emit('snooze-to-do', notification, option.value)">
              <v-list-item-icon><v-icon small>{{ option.icon }}</v-icon></v-list-item-icon>
              <v-list-item-title>{{ option.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <button v-if="count > 1" :disabled="busy" class="ntf-chip" type="button" @click="$emit('dismiss-group', entry)">
          Alle {{ count }} entfernen
        </button>
      </div>
    </div>
    <span v-if="!notification.readAt" aria-label="Ungelesen" class="ntf-row__dot" role="img"/>
    <div class="ntf-row__actions" @click.stop @keydown.stop>
      <a v-for="link in view.links" :key="link.path" :aria-label="link.label" :href="$router.resolve(link.path).href"
         v-tip="link.label" class="ntf-act" rel="noopener" target="_blank">
        <v-icon small>{{ link.icon }}</v-icon>
      </a>
      <to-do-notification-acquisition-lists-link-button v-if="view.acquisitionLists" :notification="notification"
                                                        class="ntf-act-wrap" show-tooltip/>
      <button v-if="canMarkUnread" aria-label="Als ungelesen markieren" v-tip="'Als ungelesen markieren'" class="ntf-act"
              type="button" @click="$emit('mark-unread', entry)">
        <v-icon small>mdi-email-mark-as-unread</v-icon>
      </button>
      <span v-if="canSnooze" class="ntf-act-wrap">
        <notification-snooze-menu @snooze="$emit('snooze', entry, $event)"/>
      </span>
      <button v-if="!notification.dismissedAt" aria-label="In den Papierkorb" v-tip="'In den Papierkorb'" class="ntf-act"
              type="button" @click="$emit('dismiss', entry)">
        <v-icon small>mdi-delete-outline</v-icon>
      </button>
      <button v-else aria-label="Wiederherstellen" v-tip="'Wiederherstellen'" class="ntf-act" type="button"
              @click="$emit('revoke', entry)">
        <v-icon small>mdi-delete-restore</v-icon>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {isToDoCompleted} from '@/components/app/appBar/notifications/toDoCompletion.helper';
import Vue from 'vue';
import {NotificationLike} from '@/types/NotificationLike.type';
import {NotificationView, relativeTime, toDoIdOf, toNotificationView} from './notificationView.helper';
import ToDoNotificationAcquisitionListsLinkButton
  from '@/components/app/appBar/notifications/appNotifications/components/todoHelpers/toDoNotificationAcquisitionListsLinkButton.vue';
import NotificationSnoozeMenu from '@/components/app/appBar/notifications/notificationSnoozeMenu.vue';
import {NotificationEntry, TO_DO_ACTION_TYPES} from '@/helpers/notificationGroups.helper';
import {TO_DO_SNOOZE_OPTIONS} from '@/components/toDos/toDoActions.helper';
import {toDoNotificationAccess} from '@/components/app/appBar/notifications/notificationActions.helper';
import {backendFeatures} from '@/helpers/featureProbe.helper';
import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {
  canOpenNotification,
  openFailureText,
  openNotificationInNewTab,
  openNotificationTarget,
  resolveNotificationPath,
} from '@/components/app/appBar/notifications/notificationNavigation.helper';

/**
 * One row of the notification panel: icon by type, title, text, relative time, unread dot and hover actions.
 * Repeated overdue reminders of one to-do arrive as one entry (count, "Alle N entfernen"); to-do rows offer
 * Erledigen / Neues Datum. Events carry the entry (all notifications of the row).
 */
export default Vue.extend({
  components: {NotificationSnoozeMenu, ToDoNotificationAcquisitionListsLinkButton},
  props: {
    entry: {
      type: Object as () => NotificationEntry,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    /** Current time (ms), updated every minute by the panel so the relative time stays current */
    now: {
      type: Number,
      default: () => Date.now(),
    },
  },
  data: () => ({
    /** The to-do behind the notification is already completed (e.g. by the QM backend) */
    toDoDone: false,
    opening: false,
  }),
  computed: {
    notification(): NotificationLike {
      return this.entry.notification;
    },
    count(): number {
      return this.entry.members.length;
    },
    toDoSnoozeOptions: () => TO_DO_SNOOZE_OPTIONS,
    /** Open (not deleted) to-do notifications offer Erledigen / Neues Datum in the row */
    toDoActions(): boolean {
      return !this.notification.dismissedAt && TO_DO_ACTION_TYPES.includes(this.notification.type) && !!toDoIdOf(this.notification);
    },
    toDoAccess(): { complete: boolean; snooze: boolean } {
      return toDoNotificationAccess(this.notification);
    },
    canMarkUnread(): boolean {
      return backendFeatures.notifierV2 === true && !!this.notification.readAt && !this.notification.dismissedAt;
    },
    canSnooze(): boolean {
      return backendFeatures.notifierV2 === true && !this.notification.dismissedAt && this.notification.type !== NotificationTypes.CALL;
    },
    view(): NotificationView {
      return toNotificationView(this.notification);
    },
    /** Mentions without a direct path (responsible, incomplete data) are resolved on click */
    canOpen(): boolean {
      return canOpenNotification(this.notification);
    },
    time(): string {
      return relativeTime(this.notification, this.now);
    },
  },
  methods: {
    async checkToDoDone() {
      const id = this.toDoActions ? toDoIdOf(this.notification) : undefined;
      this.toDoDone = id ? await isToDoCompleted(String(id)) : false;
    },
    /**
     * Opens the target; with Ctrl/Cmd in a new tab. The panel closes (`navigate`) only after the page was opened,
     * otherwise it stays open with a snackbar.
     */
    async open(event: MouseEvent | KeyboardEvent) {
      if (!this.canOpen || this.opening) {
        return;
      }
      const newTab = event.metaKey || event.ctrlKey;
      this.opening = true;
      try {
        const path = await resolveNotificationPath(this.notification);
        if (!path) {
          this.$$showSnackbar(openFailureText('missing'), 'warning');
          return;
        }
        if (newTab) {
          openNotificationInNewTab(this.$router, path);
          return;
        }
        const result = await openNotificationTarget(this.$router, path);
        if (result === 'opened') {
          this.$emit('navigate');
        } else {
          this.$$showSnackbar(openFailureText(result), 'warning');
        }
      } finally {
        this.opening = false;
      }
    },
  },
  mounted() {
    this.checkToDoDone();
  },
});
</script>

<style scoped>
.ntf-row.ntf-row--opening {
  cursor: progress;
  opacity: .7;
}

.ntf-row {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 10px;
  margin: 0 6px;
  color: var(--sh-ink);
  font-size: 13px;
  outline: none;
}

.ntf-row--link {
  cursor: pointer;
}

.ntf-row:hover, .ntf-row:focus-visible, .ntf-row:focus-within {
  background: var(--sh-soft);
}

.ntf-row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--sh-blue);
}

.ntf-row__icon {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
}

.ntf-row__icon .v-icon {
  color: inherit !important;
}

.ntf-row__icon--info {
  background: #E8F1FD;
  color: #0D47A1;
}

.ntf-row__icon--good {
  background: #E7F6EC;
  color: #1B5E20;
}

.ntf-row__icon--warn {
  background: #FFF4DB;
  color: #7A4F00;
}

.ntf-row__icon--bad {
  background: #FDECEC;
  color: #B71C1C;
}

.ntf-row__icon--mention {
  background: #F1ECFB;
  color: #4527A0;
}

.ntf-row__icon--neutral {
  background: var(--sh-soft);
  color: var(--sh-muted);
}

.ntf-row__body {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 12px;
}

.ntf-row__title {
  font-weight: 500;
  line-height: 1.35;
}

.ntf-row--unread .ntf-row__title {
  font-weight: 600;
}

.ntf-row__text, .ntf-row__line {
  margin-top: 2px;
  color: var(--sh-ink);
  opacity: .82;
  line-height: 1.4;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ntf-row__line {
  color: var(--sh-muted);
  opacity: 1;
  font-size: 12px;
}

.ntf-row__hint {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
}

.ntf-row__hint--bad {
  color: var(--sh-bad);
}

.ntf-row__hint--good {
  color: #1B5E20;
}

.ntf-row__count {
  display: inline-block;
  margin-left: 4px;
  padding: 0 7px;
  border-radius: 999px;
  background: #FDECEC;
  color: #B71C1C;
  font-size: 11px;
  font-weight: 700;
}

.theme--dark .ntf-row__count {
  background: rgba(239, 154, 154, .13);
  color: #EF9A9A;
}

.ntf-row__todo {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.ntf-chip {
  height: 26px;
  padding: 0 9px;
  border-radius: 7px;
  border: 0;
  background: var(--sh-soft);
  color: var(--sh-ink);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.ntf-chip .v-icon {
  color: inherit !important;
}

.ntf-chip--go {
  background: var(--sh-blue-soft);
  color: var(--sh-blue);
  font-weight: 500;
}

.ntf-chip:disabled {
  opacity: .6;
  cursor: default;
}

.ntf-chip:focus-visible {
  outline: 2px solid var(--sh-blue);
  outline-offset: 2px;
}

.ntf-row__time {
  margin-top: 3px;
  color: var(--sh-muted);
  font-size: 12px;
}

.ntf-row__time .v-icon {
  color: inherit !important;
}

.ntf-row__dot {
  position: absolute;
  top: 16px;
  right: 14px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--sh-dot);
}

.ntf-row__actions {
  position: absolute;
  top: 6px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 10px;
  background: var(--sh-panel);
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08), 0 4px 12px rgba(15, 23, 42, .1);
  opacity: 0;
  pointer-events: none;
  transition: opacity .12s ease;
}

.ntf-row:hover .ntf-row__actions, .ntf-row:focus-within .ntf-row__actions {
  opacity: 1;
  pointer-events: auto;
}

.ntf-act, .ntf-act-wrap ::v-deep .v-btn, .ntf-act-wrap ::v-deep .ntf-act {
  width: 30px !important;
  height: 30px !important;
  border: 0;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: transparent;
  color: var(--sh-muted) !important;
  cursor: pointer;
  text-decoration: none;
}

.ntf-act-wrap ::v-deep .v-btn .v-icon {
  font-size: 18px !important;
}

.ntf-act:hover, .ntf-act:focus-visible, .ntf-act-wrap ::v-deep .v-btn:hover, .ntf-act-wrap ::v-deep .ntf-act:hover {
  background: var(--sh-soft);
  color: var(--sh-ink) !important;
}

.ntf-act .v-icon {
  color: inherit !important;
}

/* Touch devices have no hover: actions stay visible below the text */
@media (hover: none) {
  .ntf-row {
    flex-wrap: wrap;
  }

  .ntf-row__actions {
    position: static;
    opacity: 1;
    pointer-events: auto;
    box-shadow: none;
    background: transparent;
    margin-left: 44px;
  }
}

.theme--dark .ntf-row__icon--info {
  background: rgba(100, 181, 246, .16);
  color: #90CAF9;
}

.theme--dark .ntf-row__icon--good {
  background: rgba(102, 187, 106, .16);
  color: #A5D6A7;
}

.theme--dark .ntf-row__icon--warn {
  background: rgba(255, 183, 77, .16);
  color: #FFD180;
}

.theme--dark .ntf-row__icon--bad {
  background: rgba(239, 83, 80, .16);
  color: #EF9A9A;
}

.theme--dark .ntf-row__icon--mention {
  background: rgba(186, 104, 200, .16);
  color: #CE93D8;
}

.theme--dark .ntf-row__hint--good {
  color: #A5D6A7;
}

.theme--dark .ntf-row__actions {
  box-shadow: 0 1px 2px rgba(0, 0, 0, .4), 0 4px 12px rgba(0, 0, 0, .35);
}
</style>
